import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { UnitIntelligenceService } from 'src/app/services/unit-intelligence.service';
import { UnitService } from 'src/app/services/unit.service';
import { UtilsService } from 'src/app/services/utils.service';

const UNIT_TYPE_LABELS: Record<number, string> = {
    1: 'Tipo Partner',
    2: 'Tipo Panel',
    3: 'Tipo crafter',
    4: 'RAM',
    5: 'Torton',
    6: 'Tracto',
};

/** Calculado una sola vez al cargar el módulo — nunca dentro de un getter
 * leído por *ngFor, porque Angular lo reevalúa en cada ciclo de detección
 * de cambios y, al construir objetos nuevos cada vez, *ngFor destruye y
 * recrea todo el DOM sin parar (así se congeló el selector de fechas antes
 * de arreglarlo con el mismo patrón). */
const UNIT_TYPE_OPTIONS = Object.entries(UNIT_TYPE_LABELS).map(([id, label]) => ({ id: Number(id), label }));

@Component({
    selector: 'app-unit-maintenance-history',
    templateUrl: './unit-maintenance-history.component.html',
    styleUrls: ['./unit-maintenance-history.component.css']
})
export class UnitMaintenanceHistoryComponent implements OnInit {

    readonly Math = Math;

    unitId = 0;

    filterFrom = '';
    filterTo = '';
    filterUnitTypeId = '';
    filterCategory = '';
    filterStatus = '';

    units: any[] = [];
    filteredUnits: any[] = [];
    readonly unitTypeOptions = UNIT_TYPE_OPTIONS;

    data: any = null;
    loading = false;

    currentPage = 1;
    perPage = 10;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private svc: UnitIntelligenceService,
        private unitService: UnitService,
        private spinner: NgxSpinnerService,
        private utils: UtilsService
    ) { }

    ngOnInit(): void {
        const today = new Date();
        const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        this.filterFrom = firstOfMonth.toISOString().slice(0, 10);
        this.filterTo = today.toISOString().slice(0, 10);

        this.unitService.getInfo().subscribe({
            next: (resp: any) => {
                this.units = Array.isArray(resp) ? resp : (resp?.data || []);
                this.updateFilteredUnits();
            }
        });

        this.route.params.subscribe(params => {
            this.unitId = Number(params['id']);
            this.currentPage = 1;
            this.fetch();
        });
    }

    private updateFilteredUnits(): void {
        this.filteredUnits = !this.filterUnitTypeId
            ? this.units
            : this.units.filter(u => String(u.type) === String(this.filterUnitTypeId));
    }

    onUnitTypeFilterChanged(): void {
        this.updateFilteredUnits();
    }

    fetch(): void {
        this.loading = true;
        this.spinner.show();
        const params: any = {
            from: this.filterFrom,
            to: this.filterTo,
            category: this.filterCategory,
            status: this.filterStatus,
            page: this.currentPage,
            per_page: this.perPage,
        };

        this.svc.getMaintenanceHistory(this.unitId, params).subscribe({
            next: (resp: any) => {
                this.data = resp;
                this.loading = false;
                this.spinner.hide();
            },
            error: () => {
                this.loading = false;
                this.spinner.hide();
                this.utils.alertSwal({ title: 'Error', text: 'No se pudo obtener el histórico de mantenimiento', icon: 'error' });
            }
        });
    }

    applyFilters(): void {
        this.currentPage = 1;
        this.fetch();
    }

    onUnitChanged(newUnitId: string): void {
        if (!newUnitId) return;
        this.router.navigate(['/unit/maintenance-history', newUnitId]);
    }

    goToPage(page: number): void {
        if (page < 1 || (this.data?.history?.pagination?.last_page && page > this.data.history.pagination.last_page)) return;
        this.currentPage = page;
        this.fetch();
    }

    get pageNumbers(): number[] {
        const lastPage = this.data?.history?.pagination?.last_page || 1;
        const windowSize = 3;
        const end = Math.min(lastPage, Math.max(1, this.currentPage - 1) + windowSize - 1);
        const start = Math.max(1, end - windowSize + 1);
        const pages: number[] = [];
        for (let i = start; i <= end; i++) pages.push(i);
        return pages;
    }

    goBack(): void {
        this.router.navigateByUrl('/unit/list');
    }

    formatCurrency(value: number | null | undefined): string {
        if (value === null || value === undefined) return '—';
        return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(value);
    }

    formatKm(value: number | null | undefined): string {
        if (value === null || value === undefined) return '—';
        return `${new Intl.NumberFormat('es-MX').format(value)} km`;
    }

    formatDate(value: string | null | undefined): string {
        if (!value) return '—';
        const [yyyy, mm, dd] = value.split('T')[0].split('-');
        return `${dd}/${mm}/${yyyy}`;
    }

    formatDateLong(value: string | null | undefined): string {
        if (!value) return '—';
        const [yyyy, mm, dd] = value.split('T')[0].split('-');
        const months = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
        return `${Number(dd)} ${months[Number(mm) - 1]} ${yyyy}`;
    }

    statusPillClass(tone: string): string {
        return 'mh-status-pill--' + tone;
    }
}
